import { ReactElement, useMemo } from 'react';
import { DrawerPanel } from '@dmc/ui';
import {
  SafetyAlertModel,
  useAcknowledgeSafetyAlertMutation,
} from '@casops/store/apis/personnelSafetyApi';
import { SafetyAlertHeader } from '../components/header/SafetyAlertHeader';
import { SafetyAlertMap } from '@casops/components/maps/safety-alert-map/SafetyAlertMap';
import useTabs, { TabConfig } from '@cas/components/tabs/tabs/useTabs';
import { useTranslation } from 'react-i18next';
import Tabs from '@cas/components/tabs/tabs/Tabs';
import styles from './SafetyAlertManagerView.module.scss';
import { VdsButton } from '@volt/volt-extended-components-react';
import { useModelActionDialog } from '@casops/dialogs/model-action-dialog/useModelActionDialog';
import { ModelWithId, SafetyAlertType } from '@casops/store/apis/commonModels';
import { ModelAction, ModelName } from '@casops/store/api';
import ModelActionDialog from '@casops/dialogs/model-action-dialog/ModelActionDialog';
import { SafetyAlertNotifications } from '../components/safety-alert-notifications/SafetyAlertNotifications';
import { AcknowledgedStatement } from '../components/acknowledged-statement/AcknowledgedStatement';

const modelAction = ModelAction.Acknowledge;

interface SafetyAlertManagerViewProps {
  item: SafetyAlertModel;
}

export function SafetyAlertManagerView({ item }: SafetyAlertManagerViewProps): ReactElement {
  const { t } = useTranslation();

  const modelName =
    item.typ === SafetyAlertType.Duress
      ? ModelName.SafetyAlertDuress
      : ModelName.SafetyAlertManDown;

  const tabConfigs = useMemo<TabConfig[]>(() => {
    return [
      {
        title: t('Navigation.Safety.Event'),
        view: <SafetyAlertMap item={item} />,
      },
      {
        title: t('Navigation.Safety.Notifications'),
        view: <SafetyAlertNotifications item={item} />,
      },
    ];
  }, [t, item]);

  const { activeView, tabs, activeTab, onSelectTab } = useTabs({
    tabConfigs,
  });

  const { dialogRef, onAction, openDialog, isError, isLoading } = useModelActionDialog<ModelWithId>(
    {
      queryArg: { id: item.id },
      modelName,
      modelAction,
      useModelMutation: useAcknowledgeSafetyAlertMutation,
    },
  );

  const isEventTab = activeTab === 0;

  return (
    <>
      <DrawerPanel header={<SafetyAlertHeader model={item} />}>
        <>
          <Tabs tabs={tabs} activeTab={activeTab} onSelectTab={onSelectTab} hasBorder={true} />
          <div className={styles.tabView}>
            {activeView}
            {isEventTab ? (
              item.ack ? (
                <AcknowledgedStatement ackAt={item.ackAt} ackBy={item.ackBy} />
              ) : (
                <VdsButton variant="primary" danger={true} onClick={openDialog} isFullWidth>
                  {t(`Model.${modelAction}.ActionLabel`)}
                </VdsButton>
              )
            ) : null}
          </div>
        </>
      </DrawerPanel>
      <ModelActionDialog
        elementRef={dialogRef}
        modelName={modelName}
        modelAction={modelAction}
        onAction={onAction}
        isError={isError}
        isLoading={isLoading}
        danger={true}
      />
    </>
  );
}

export default SafetyAlertManagerView;
